import {
  AppPlatform,
  AppRegion,
  InitialTrafficData,
  ParsedAppItem,
  ParsedTrafficData,
  ParsedTrafficItem,
} from './types'

export function getParsedDataFromInitial(data: InitialTrafficData): ParsedTrafficData {
  const { mainDomain, trafficsWithPeers, appsWithPeers, companyName, trafficTickers } = data

  return {
    trafficTickers,
    companyName,
    mainDomain: trafficsWithPeers.find(({ domain }) => domain && mainDomain?.includes(domain))?.domain ?? null,
    trafficsWithPeers: trafficsWithPeers.map(({ trafficJson, ...item }) => ({
      ...item,
      traffic: trafficJson ? JSON.parse(trafficJson) : null,
    })),
    appsWithPeers: appsWithPeers.map(({ mauJson, ...item }) => {
      let geo: AppRegion | null = null
      let os: AppPlatform | null = null
      if (item.geo?.toLowerCase() === AppRegion.ww) {
        geo = AppRegion.ww
      } else if (item.geo?.toLowerCase() === AppRegion.us) {
        geo = AppRegion.us
      }
      if (item.os?.toLowerCase() === AppPlatform.ios) {
        os = AppPlatform.ios
      } else if (item.os?.toLowerCase() === AppPlatform.android) {
        os = AppPlatform.android
      }

      return {
        ...item,
        appName: item.appName ?? 'N/A',
        id: `${item.domain}-${item.appName}-${item.appId}-${item.os}-${item.geo}`,
        os,
        geo,
        mau: mauJson ? JSON.parse(mauJson) : null,
      }
    }),
  }
}

export function getSortedTickerTrafficsWithPeers(parsedData: ParsedTrafficData) {
  const tickerTrafficsWithPeers = parsedData.trafficsWithPeers.filter(
    (trafficWithPeers) =>
      trafficWithPeers.domain &&
      parsedData.trafficTickers.some((ticker) => ticker.toLowerCase() === trafficWithPeers.ticker?.toLowerCase())
  )
  tickerTrafficsWithPeers.sort((a, b) => {
    if (a.domain === parsedData?.mainDomain) return -1
    if (b.domain === parsedData?.mainDomain) return 1
    const aLastValue = a.traffic?.v[a.traffic.v.length - 1] ?? 0
    const bLastValue = b.traffic?.v[b.traffic.v.length - 1] ?? 0
    if (aLastValue === bLastValue) return 0

    return aLastValue > bLastValue ? -1 : 1
  })
  return tickerTrafficsWithPeers
}

export function getAppStoreLink(app: ParsedAppItem) {
  if (!app.appId) return undefined

  if (app.os === AppPlatform.android) {
    return `https://play.google.com/store/apps/details?id=${app.appId}`
  }

  return `https://apps.apple.com/app/${app.appId}`
}

export function getDomainHref(item: ParsedTrafficItem) {
  if (item.domain) {
    return `https://${item.domain}`
  }
  return undefined
}

interface GetSelectedAppsArrayProps {
  domainApps: ParsedAppItem[]
  peerApps: ParsedAppItem[]
  industryApps: ParsedAppItem[]
  appRegions: AppRegion[]
  appPlatforms: AppPlatform[]
}

export function getSelectedAppsArray({
  domainApps,
  peerApps,
  industryApps,
  appPlatforms,
  appRegions,
}: GetSelectedAppsArrayProps) {
  if (domainApps.length === 0) {
    return []
  }

  return [domainApps, peerApps, industryApps]
    .flat()
    .filter((app) => appPlatforms.includes(app.os!) && appRegions.includes(app.geo!))
}

export function sortByMauLastValueDesc(apps: ParsedAppItem[]) {
  return [...apps].sort((a, b) => {
    const aLastValue = a.mau?.v[a.mau.v.length - 1] ?? 0
    const bLastValue = b.mau?.v[b.mau.v.length - 1] ?? 0
    if (aLastValue === bLastValue) return 0

    return aLastValue > bLastValue ? -1 : 1
  })
}
